import {
  ActionIcon,
  Button,
  Card,
  Checkbox,
  Divider,
  Group,
  NumberInput,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconHelpCircle, IconPlus, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import type { AuthLimitTypeCode } from '@unify/fhir-model/constants';
import type { InsuranceAuthorizationRecord } from '@/lib/billing/insuranceAuthorization';
import { toFhirDateOnlyLocal } from '@/lib/dateHelpers';

type AuthorizationDraft = InsuranceAuthorizationRecord & { clientKey: string };

function parseAuthDate(value?: string): Date | null {
  if (!value) return null;
  return new Date(`${value}T12:00:00`);
}

function formatAuthDate(value: Date | string | null): string | undefined {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  return toFhirDateOnlyLocal(value);
}

function limitTypeForUses(uses?: number): AuthLimitTypeCode {
  if (uses == null || uses <= 0) return 'date-range';
  return 'limited';
}

function toDraft(record: InsuranceAuthorizationRecord): AuthorizationDraft {
  return { ...record, clientKey: record.id || `draft-${Math.random().toString(16).slice(2)}` };
}

function newDraft(patientRef: string, coverageRef?: string): AuthorizationDraft {
  return {
    clientKey: `new-${Math.random().toString(16).slice(2)}`,
    id: '',
    patientRef,
    coverageRef,
    authorizationNumber: '',
    limitType: 'date-range',
    usedCount: 0,
    status: 'active',
    remindMonthBeforeExpiring: false,
    manualInactive: false,
  };
}

function isSaveable(draft: AuthorizationDraft): boolean {
  return Boolean(draft.authorizationNumber.trim() && draft.validFrom);
}

export interface AuthorizationTrackingSectionProps {
  authorizations: InsuranceAuthorizationRecord[];
  patientRef: string;
  defaultCoverageRef?: string;
  saving?: boolean;
  onSave: (draft: InsuranceAuthorizationRecord) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

export function AuthorizationTrackingSection({
  authorizations,
  patientRef,
  defaultCoverageRef,
  saving = false,
  onSave,
  onDelete,
}: AuthorizationTrackingSectionProps): JSX.Element {
  const [drafts, setDrafts] = useState<AuthorizationDraft[]>([]);

  useEffect(() => {
    setDrafts((prev) => {
      const pendingNew = prev.filter((d) => !d.id);
      return [...authorizations.map(toDraft), ...pendingNew];
    });
  }, [authorizations]);

  const updateDraft = (clientKey: string, patch: Partial<AuthorizationDraft>) => {
    setDrafts((prev) => prev.map((d) => (d.clientKey === clientKey ? { ...d, ...patch } : d)));
  };

  const persistDraft = async (draft: AuthorizationDraft) => {
    if (!isSaveable(draft)) return;
    const authorizedUses = draft.authorizedUses;
    const normalized: InsuranceAuthorizationRecord = {
      ...draft,
      coverageRef: draft.coverageRef ?? defaultCoverageRef,
      limitType: limitTypeForUses(authorizedUses),
      authorizedUses: authorizedUses && authorizedUses > 0 ? authorizedUses : undefined,
    };
    await onSave(normalized);
  };

  const persistClientKey = (clientKey: string) => {
    queueMicrotask(() => {
      setDrafts((prev) => {
        const current = prev.find((d) => d.clientKey === clientKey);
        if (current) void persistDraft(current);
        return prev;
      });
    });
  };

  const handleAdd = () => {
    setDrafts((prev) => [...prev, newDraft(patientRef, defaultCoverageRef)]);
  };

  const handleDelete = async (draft: AuthorizationDraft) => {
    if (draft.id) {
      await onDelete(draft.id);
      return;
    }
    setDrafts((prev) => prev.filter((d) => d.clientKey !== draft.clientKey));
  };

  return (
    <Card withBorder>
      <Title order={4} mb="md">
        Authorization tracking
      </Title>

      {drafts.length === 0 ? (
        <Text size="sm" c="gray.6" mb="md">
          No authorizations on file.
        </Text>
      ) : (
        <Stack gap="lg" mb="md">
          {drafts.map((draft, index) => (
            <Stack key={draft.clientKey} gap="sm">
              <Group align="flex-start" wrap="nowrap" gap="md">
                <TextInput
                  label="Prior authorization number"
                  required
                  withAsterisk
                  style={{ flex: 1 }}
                  value={draft.authorizationNumber}
                  disabled={saving}
                  onChange={(e) => updateDraft(draft.clientKey, { authorizationNumber: e.currentTarget.value })}
                  onBlur={() => persistClientKey(draft.clientKey)}
                />
                <DateInput
                  label="Valid from"
                  required
                  withAsterisk
                  style={{ flex: 1 }}
                  value={parseAuthDate(draft.validFrom)}
                  disabled={saving}
                  onChange={(value) => {
                    const validFrom = formatAuthDate(value);
                    updateDraft(draft.clientKey, { validFrom });
                  }}
                  onBlur={() => persistClientKey(draft.clientKey)}
                />
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  mt={28}
                  aria-label="Remove authorization"
                  disabled={saving}
                  onClick={() => void handleDelete(draft)}
                >
                  <IconTrash size={18} />
                </ActionIcon>
              </Group>

              <NumberInput
                label="Number of uses"
                style={{ maxWidth: 220 }}
                min={0}
                allowDecimal={false}
                value={draft.authorizedUses ?? ''}
                disabled={saving}
                onChange={(value) => {
                  const authorizedUses = typeof value === 'number' ? value : undefined;
                  updateDraft(draft.clientKey, {
                    authorizedUses,
                    limitType: limitTypeForUses(authorizedUses),
                  });
                }}
                onBlur={() => void persistDraft(draft)}
              />

              <Group gap="xl">
                <Checkbox
                  label="Remind me 1 month before expiring"
                  checked={draft.remindMonthBeforeExpiring ?? false}
                  disabled={saving}
                  onChange={(e) => {
                    const remindMonthBeforeExpiring = e.currentTarget.checked;
                    updateDraft(draft.clientKey, { remindMonthBeforeExpiring });
                    queueMicrotask(() => persistClientKey(draft.clientKey));
                  }}
                />
                <Checkbox
                  label={
                    <Group gap={4} wrap="nowrap">
                      <span>Active</span>
                      <Tooltip
                        label="Inactive authorizations are kept on file but excluded from active billing checks."
                        withArrow
                      >
                        <IconHelpCircle size={14} style={{ opacity: 0.55 }} />
                      </Tooltip>
                    </Group>
                  }
                  checked={!draft.manualInactive}
                  disabled={saving}
                  onChange={(e) => {
                    const manualInactive = !e.currentTarget.checked;
                    updateDraft(draft.clientKey, { manualInactive });
                    queueMicrotask(() => persistClientKey(draft.clientKey));
                  }}
                />
              </Group>

              {index < drafts.length - 1 && <Divider />}
            </Stack>
          ))}
        </Stack>
      )}

      <Button
        variant="outline"
        color="blue"
        radius="xl"
        size="sm"
        leftSection={<IconPlus size={14} />}
        onClick={handleAdd}
      >
        Authorization number
      </Button>
    </Card>
  );
}
